import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useKeyboardStats } from '@/components/analyze/key-reading/hooks/use-keyboard-stats';
import { useTranslation } from '@/hooks/use-translation';
import { formatRatio } from '@/lib/format/format-weight';
import type { KeyboardLike, KeyWith } from '@/lib/piano/keyboard';
import type { KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
import type { OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import type { TouchWeightKeyAnalysis } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { CHART_HEIGHTS, PLOT_BOX_MARGIN, PLOT_INSET } from './chart-geometry';
import { GRID_TOP, type AxisScale } from './echarts/build-base-option';
import { ChartEmptyFrame } from './figure/ChartEmptyFrame';
import { ChartFigure } from './figure/ChartFigure';
import { ChartKeyboardAxis } from './figure/ChartKeyboardAxis';
import { ChartLegend, type ChartLegendItem } from './figure/ChartLegend';
import { useChartFigure } from './hooks/use-chart-figure';
import { useGenerateSerie } from './hooks/use-generate-serie';
import { KeyboardChart, TouchDesignSerieVariant } from './KeyboardChart';
import { serieValueAt, type ChartReadingContext, type TouchDesignSerie } from './interfaces';
import { useLegendToggles } from './hooks/use-legend-toggles';

/** The scale of the mockups: 5.5 to 7.0, ruled every 0.5 and halved by a faint line. */
const RATIO_BAND = {
  interval: 0.5,
  max: 7,
  min: 5.5
} as const;

/** Rows of the overlay's own coordinate space; fine enough for any plot height. */
const PLOT_SPAN = 1000;
type KeyWithStrikeWeightRatio<T> = T & Pick<TouchWeightKeyAnalysis, 'computedStrikeWeightRatio' | 'measuredStrikeWeightRatio' | 'strikeWeightRatio'>;

/**
 * The quantity this chart plots.
 *
 * The chart draws the median of it and the reading states that same median,
 * so both read the keyboard through this one selector.
 */
export const selectComputedStrikeWeightRatio = (key: KeyWith<Pick<TouchWeightKeyAnalysis, 'computedStrikeWeightRatio'>>): OptionalNumber => key.payload.computedStrikeWeightRatio;

/**
 * The ratio the rest of the application works from: what was measured where
 * it was measured, and what the analysis derives everywhere else.
 *
 * The data sheet prints it, the design smooths it and the preview computes
 * from it, so it is what a single plotted curve has to be — otherwise the
 * figure and the sheet state two different numbers for one quantity.
 */
export const selectStrikeWeightRatio = (key: KeyWith<Pick<TouchWeightKeyAnalysis, 'strikeWeightRatio'>>): OptionalNumber => key.payload.strikeWeightRatio;

/**
 * The mockup band, widened to whatever the instrument actually reads.
 *
 * 5.5–7.0 covers a well-regulated grand, but an unusual action reads outside
 * it and its keys must not drop off the plot: the band is a floor, never a
 * ceiling.
 */
const buildRatioScale = (series: readonly TouchDesignSerie[]): AxisScale => {
  const {
    interval
  } = RATIO_BAND;
  const values = series.flatMap(serie => serie.data.map(point => point.payload).filter((value): value is number => value !== undefined && !Number.isNaN(value)));
  return {
    interval,
    max: Math.max(RATIO_BAND.max, ...values.map(value => Math.ceil(value / interval) * interval)),
    min: Math.min(RATIO_BAND.min, ...values.map(value => Math.floor(value / interval) * interval)),
    minorSplit: true
  };
};
interface Stem {
  from: number;
  to: number;
  /** Horizontal position of the key, in white-key units. */
  x: number;
}

/**
 * The stems tying each point to what it is read against, and the brass rule
 * of the median.
 *
 * `KeyboardChart` draws one series per quantity and offers no slot for either
 * of these, so they are drawn here instead, over the plot rectangle the chart
 * lays out. It is pinned to the insets that rectangle keeps rather than given
 * a height of its own, so that it still covers the plot when the figure is
 * given the height of a sheet. Being SVG and HTML they also follow the theme
 * through CSS, as the hover band does.
 */
const ChartReferenceOverlay = t0 => {
  const $ = _c(26);
  const {
    geometry,
    median,
    medianLabel,
    scale,
    stems
  } = t0;
  let t1;
  if ($[0] !== scale.max || $[1] !== scale.min) {
    t1 = value => (scale.max - value) / (scale.max - scale.min) * PLOT_SPAN;
    $[0] = scale.max;
    $[1] = scale.min;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const row = t1;
  if (geometry.width === 0) {
    return null;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      bottom: PLOT_BOX_MARGIN - GRID_TOP,
      left: PLOT_INSET.left,
      right: PLOT_INSET.right,
      top: GRID_TOP
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `0 0 ${geometry.width} ${PLOT_SPAN}`;
  let t4;
  if ($[4] !== row || $[5] !== stems) {
    let t5;
    if ($[7] !== row) {
      t5 = stem => `M${stem.x} ${row(stem.from)}V${row(stem.to)}`;
      $[7] = row;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = stems.map(t5).join("");
    $[4] = row;
    $[5] = stems;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <path className="stroke-ptl-series-standard-soft" d={t4} fill="none" strokeWidth={1} vectorEffect="non-scaling-stroke" />;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== geometry.width || $[12] !== median || $[13] !== row) {
    t6 = median === null ? null : <path className="stroke-ptl-brass" d={`M0 ${row(median)}H${geometry.width}`} fill="none" strokeDasharray="6 4" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />;
    $[11] = geometry.width;
    $[12] = median;
    $[13] = row;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t6) {
    t7 = <svg className="block size-full" preserveAspectRatio="none" viewBox={t3}>{t5}{t6}</svg>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== median || $[20] !== medianLabel || $[21] !== row) {
    t8 = median === null ? null : <span className="absolute -translate-y-1/2 font-mono text-[11px] whitespace-nowrap text-ptl-brass" style={{
      left: "calc(100% + 6px)",
      top: `${row(median) / 10}%`
    }}>{medianLabel}</span>;
    $[19] = median;
    $[20] = medianLabel;
    $[21] = row;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <div aria-hidden="true" className="pointer-events-none absolute z-0" style={t2}>{t7}{t8}</div>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export interface StrikeWeightRatioChartProps<T> {
  /** Said over the empty figure, until there is something to draw. */
  emptyState?: React.ReactNode;
  /** Draws the hand-measured ratio beside the computed one. */
  displayBothManualAndComputed: boolean;
  /** Legend entries contributed by the page, e.g. the design target. */
  extraLegendItems?: ChartLegendItem[];
  keyboard: KeyboardLike<KeyWith<KeyWithStrikeWeightRatio<T>>>;
  /**
   * The reading strip at the head of the figure. It is a function so that the
   * strip reads the very series the chart draws, rather than computing them
   * again.
   */
  renderReading?: (context: ChartReadingContext) => React.ReactNode;
  targetSerie?: TouchDesignSerie;
  variant?: 'analyze' | 'design';
}
export const StrikeWeightRatioChart = t0 => {
  const $ = _c(109);
  const {
    emptyState,
    displayBothManualAndComputed,
    extraLegendItems,
    keyboard,
    renderReading,
    targetSerie,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    geometry,
    hover,
    keys
  } = useChartFigure(keyboard);
  const {
    isSerieEmpty,
    generateSerie
  } = useGenerateSerie(keyboard);
  const stats = useKeyboardStats(keyboard, displayBothManualAndComputed ? selectComputedStrikeWeightRatio : selectStrikeWeightRatio);
  let t2;
  if ($[0] !== displayBothManualAndComputed || $[1] !== t) {
    t2 = displayBothManualAndComputed ? t("Computed Strike Weight Ratio") : t("Strike Weight Ratio");
    $[0] = displayBothManualAndComputed;
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const computedSerieLabel = t2;
  const t3 = displayBothManualAndComputed ? selectComputedStrikeWeightRatio : selectStrikeWeightRatio;
  let t4;
  if ($[3] !== computedSerieLabel || $[4] !== generateSerie || $[5] !== t3) {
    t4 = generateSerie(t3, computedSerieLabel, TouchDesignSerieVariant.Measured);
    $[3] = computedSerieLabel;
    $[4] = generateSerie;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const computedSerie = t4;
  let t5;
  if ($[7] !== computedSerie || $[8] !== isSerieEmpty) {
    t5 = isSerieEmpty(computedSerie);
    $[7] = computedSerie;
    $[8] = isSerieEmpty;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isEmpty = t5;
  let t6;
  if ($[10] !== generateSerie || $[11] !== t) {
    t6 = generateSerie(_temp, t("Measured Strike Weight Ratio"), TouchDesignSerieVariant.Computed);
    $[10] = generateSerie;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const manualSerie = t6;
  let t7;
  if ($[13] !== isSerieEmpty || $[14] !== manualSerie) {
    t7 = isSerieEmpty(manualSerie);
    $[13] = isSerieEmpty;
    $[14] = manualSerie;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const isManualEmpty = t7;
  const showsManual = displayBothManualAndComputed && !isManualEmpty;
  let t8;
  if ($[16] !== targetSerie) {
    t8 = targetSerie ? [targetSerie] : [];
    $[16] = targetSerie;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== computedSerie || $[19] !== isEmpty) {
    t9 = isEmpty ? [] : [computedSerie];
    $[18] = computedSerie;
    $[19] = isEmpty;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== manualSerie || $[22] !== showsManual) {
    t10 = showsManual ? [manualSerie] : [];
    $[21] = manualSerie;
    $[22] = showsManual;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8 || $[26] !== t9) {
    t11 = [...t8, ...t9, ...t10].filter(_temp2);
    $[24] = t10;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const series = t11;
  let t12;
  if ($[28] !== series) {
    t12 = buildRatioScale(series);
    $[28] = series;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const scale = t12;
  const {
    isShown,
    withToggles
  } = useLegendToggles();
  let t13;
  if ($[30] !== computedSerie || $[31] !== isShown || $[32] !== manualSerie || $[33] !== series) {
    t13 = series.filter(serie_0 => isShown(serie_0 === computedSerie ? "computed" : serie_0 === manualSerie ? "manual" : "target"));
    $[30] = computedSerie;
    $[31] = isShown;
    $[32] = manualSerie;
    $[33] = series;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const drawnSeries = t13;
  const medianRule = targetSerie ? null : stats.median;
  let t14;
  if ($[35] !== computedSerie || $[36] !== isShown) {
    t14 = isShown("computed") ? computedSerie.data : [];
    $[35] = computedSerie;
    $[36] = isShown;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== geometry || $[39] !== stats.median || $[40] !== t14 || $[41] !== targetSerie) {
    let t16;
    if ($[43] !== geometry || $[44] !== stats.median || $[45] !== targetSerie) {
      t16 = point => {
        const from = targetSerie ? serieValueAt(targetSerie, point.number) : stats.median;
        const x = geometry.keysByNumber.get(point.number)?.backCenter;
        if (from === null || point.payload === undefined || x === undefined) {
          return [];
        }
        return [{
          from,
          to: point.payload,
          x
        }];
      };
      $[43] = geometry;
      $[44] = stats.median;
      $[45] = targetSerie;
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    t15 = t14.flatMap(t16);
    $[38] = geometry;
    $[39] = stats.median;
    $[40] = t14;
    $[41] = targetSerie;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const stems = t15;
  let t16;
  if ($[47] !== computedSerieLabel || $[48] !== extraLegendItems || $[49] !== isEmpty || $[50] !== medianRule || $[51] !== showsManual || $[52] !== t || $[53] !== withToggles) {
    t16 = withToggles([...(extraLegendItems ?? []), ...(isEmpty ? [] : [{
      colorClassName: "text-foreground",
      id: "computed",
      label: computedSerieLabel,
      mark: "dot" as const
    }]), ...(showsManual ? [{
      colorClassName: "text-muted-foreground",
      id: "manual",
      label: t("Measured Strike Weight Ratio"),
      mark: "diamond" as const
    }] : []), ...(medianRule === null ? [] : [{
      colorClassName: "text-ptl-brass",
      id: "median",
      label: t("Median"),
      mark: "line-dashed" as const
    }])]);
    $[47] = computedSerieLabel;
    $[48] = extraLegendItems;
    $[49] = isEmpty;
    $[50] = medianRule;
    $[51] = showsManual;
    $[52] = t;
    $[53] = withToggles;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const legendItems = t16;
  const plotHeight = variant === "analyze" ? CHART_HEIGHTS.plot : CHART_HEIGHTS.plotCompact;
  if (isEmpty && emptyState) {
    let t17;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = buildRatioScale([]);
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    let t18;
    if ($[56] !== emptyState || $[57] !== geometry || $[58] !== keys || $[59] !== plotHeight || $[60] !== t17 || $[61] !== variant) {
      t18 = <ChartEmptyFrame geometry={geometry} height={plotHeight} keys={keys} scale={t17} variant={variant}>{emptyState}</ChartEmptyFrame>;
      $[56] = emptyState;
      $[57] = geometry;
      $[58] = keys;
      $[59] = plotHeight;
      $[60] = t17;
      $[61] = variant;
      $[62] = t18;
    } else {
      t18 = $[62];
    }
    return t18;
  }
  let t17;
  if ($[63] !== computedSerie || $[64] !== hover.hoveredKey || $[65] !== manualSerie || $[66] !== renderReading || $[67] !== showsManual) {
    t17 = renderReading?.({
      hoveredKey: hover.hoveredKey,
      measuredSerie: computedSerie,
      referenceSeries: showsManual ? [manualSerie] : []
    });
    $[63] = computedSerie;
    $[64] = hover.hoveredKey;
    $[65] = manualSerie;
    $[66] = renderReading;
    $[67] = showsManual;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  const t18 = hover.hoveredKey;
  let t19;
  if ($[69] !== legendItems) {
    t19 = legendItems.length === 0 ? undefined : <ChartLegend items={legendItems} />;
    $[69] = legendItems;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  const t20 = hover.hoveredKey;
  const t21 = hover.setHoveredKey;
  let t22;
  if ($[71] !== t) {
    t22 = t("Keyboard: hover a key to read its measurement");
    $[71] = t;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== hover.hoveredKey || $[74] !== hover.setHoveredKey || $[75] !== keys || $[76] !== t22) {
    t23 = <ChartKeyboardAxis keys={keys} highlightedKey={t20} onKeyHover={t21} description={t22} />;
    $[73] = hover.hoveredKey;
    $[74] = hover.setHoveredKey;
    $[75] = keys;
    $[76] = t22;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  const t24 = hover.figureProps;
  let t25;
  if ($[78] !== isShown || $[79] !== medianRule) {
    t25 = isShown("median") ? medianRule : null;
    $[78] = isShown;
    $[79] = medianRule;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== currentLanguage || $[82] !== medianRule) {
    t26 = formatRatio(medianRule, currentLanguage);
    $[81] = currentLanguage;
    $[82] = medianRule;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== geometry || $[85] !== scale || $[86] !== stems || $[87] !== t25 || $[88] !== t26) {
    t27 = <ChartReferenceOverlay geometry={geometry} median={t25} medianLabel={t26} scale={scale} stems={stems} />;
    $[84] = geometry;
    $[85] = scale;
    $[86] = stems;
    $[87] = t25;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== drawnSeries || $[91] !== geometry || $[92] !== hover.bindChart || $[93] !== plotHeight || $[94] !== scale || $[95] !== variant) {
    t28 = <div className="relative z-10"><KeyboardChart variant={variant} geometry={geometry} height={plotHeight} onChartReady={hover.bindChart} scale={scale} series={drawnSeries} /></div>;
    $[90] = drawnSeries;
    $[91] = geometry;
    $[92] = hover.bindChart;
    $[93] = plotHeight;
    $[94] = scale;
    $[95] = variant;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== t27 || $[98] !== t28) {
    t29 = <div className="relative">{t27}{t28}</div>;
    $[97] = t27;
    $[98] = t28;
    $[99] = t29;
  } else {
    t29 = $[99];
  }
  let t30;
  if ($[100] !== geometry || $[101] !== hover.figureProps || $[102] !== hover.hoveredKey || $[103] !== t17 || $[104] !== t19 || $[105] !== t23 || $[106] !== t29 || $[107] !== variant) {
    t30 = <ChartFigure reading={t17} variant={variant} geometry={geometry} highlightedKey={t18} header={t19} keyboard={t23} {...t24}>{t29}</ChartFigure>;
    $[100] = geometry;
    $[101] = hover.figureProps;
    $[102] = hover.hoveredKey;
    $[103] = t17;
    $[104] = t19;
    $[105] = t23;
    $[106] = t29;
    $[107] = variant;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  const figure = t30;
  return figure;
};
function _temp(key) {
  return key.payload.measuredStrikeWeightRatio;
}
function _temp2(serie) {
  return serie !== undefined;
}